import { useUserData } from '@/zustand';
import { LoadingButton } from '@mui/lab';
import { Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CatalogueBrand } from 'src/logic/models/catalogue/catalogue-content';
import { getBrandDetailsBanner } from 'src/logic/services/catalogue/brands';
import { formatInteger } from 'src/logic/utils/numbers';
import useEnvironmentFilters from 'src/logic/zustand/Filters/hooks/use-environment-filters';
import InfoPopperList from './components/InfoPopperList';
import Label from './components/Label';
import VerticalDivider from './components/VerticalDivider';

const BrandBanner = ({ brand, disableFetch = false }: { brand: CatalogueBrand; disableFetch?: boolean }) => {
  const isBuyer = useUserData.getState().isBuyer();

  const environmentFilters = useEnvironmentFilters();
  const { data, isLoading: _isLoading } = useQuery<CatalogueBrand>({
    queryKey: ['BRAND_BANNER', brand.slug ?? brand.id, { environmentFilters }],
    queryFn: async () => {
      const { data } = await getBrandDetailsBanner(brand.slug, { environmentFilters });
      return data;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 0,
    enabled: !disableFetch,
  });

  const brandBannerDetails = disableFetch ? brand : data;
  const isLoading = disableFetch ? false : _isLoading;

  return (
    <div
      style={{
        width: '100%',
        marginBlock: '1rem',
        backgroundColor: '#fff',
        borderRadius: '8px',
      }}
    >
      <div
        style={{
          padding: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
          <img
            src={brand.image_url}
            alt={brand.name}
            style={{ maxWidth: '100px', maxHeight: '60px', objectFit: 'contain' }}
          />

          <div
            style={{
              display: 'flex',
              gap: '2rem',
              alignItems: 'center',
            }}
          >
            {brandBannerDetails?.sellers !== undefined && isBuyer && (
              <>
                <div>
                  <Label label='Sellers' />
                  {isLoading ? (
                    <LoadingButton loading={true} loadingPosition='start' />
                  ) : (
                    <Typography color='#3A3E50' fontSize={14} fontWeight={600}>
                      {formatInteger(brandBannerDetails?.sellers?.length || 0)}{' '}
                      {brandBannerDetails?.sellers?.length !== 1 ? 'Sellers' : 'Seller'}{' '}
                      {brandBannerDetails?.sellers && brandBannerDetails?.sellers?.length > 0 && (
                        <InfoPopperList
                          title={brandBannerDetails?.sellers?.length !== 1 ? 'Sellers' : 'Seller'}
                          items={brandBannerDetails?.sellers}
                          renderItem={item => {
                            return (
                              <Typography
                                key={item.seller.id}
                                color='#3A3E50'
                                fontWeight={400}
                                fontSize={12}
                                lineHeight={1}
                                my={1}
                              >
                                {item.seller.name}
                              </Typography>
                            );
                          }}
                        />
                      )}
                    </Typography>
                  )}
                </div>
                <VerticalDivider />
              </>
            )}

            <>
              <div>
                <Label label='Categories' />
                {isLoading ? (
                  <LoadingButton loading={true} loadingPosition='start' />
                ) : (
                  <Typography color='#3A3E50' fontSize={14} fontWeight={600}>
                    {formatInteger(brandBannerDetails?.categories?.length || 0)}{' '}
                    {brandBannerDetails?.categories?.length !== 1 ? 'Categories' : 'Category'}{' '}
                    {brandBannerDetails?.categories && brandBannerDetails?.categories?.length > 0 && (
                      <InfoPopperList
                        title={brandBannerDetails?.categories?.length !== 1 ? 'Categories' : 'Category'}
                        items={brandBannerDetails?.categories}
                        renderItem={item => {
                          return (
                            <Typography
                              key={item.id}
                              color='#3A3E50'
                              fontWeight={400}
                              fontSize={12}
                              lineHeight={1}
                              my={1}
                            >
                              {item.name}
                            </Typography>
                          );
                        }}
                      />
                    )}
                  </Typography>
                )}
              </div>
              <VerticalDivider />
            </>

            <>
              <div>
                <Label label='Products' />
                {isLoading ? (
                  <LoadingButton loading={true} loadingPosition='start' />
                ) : (
                  <Typography color='#3A3E50' fontSize={14} fontWeight={600}>
                    {formatInteger(brandBannerDetails?.number_of_products || 0)} Product
                    {brandBannerDetails?.number_of_products !== 1 ? 's' : ''}
                  </Typography>
                )}
              </div>
            </>
          </div>
        </div>
        <Typography color='#3A3E50' fontWeight={400} fontSize={15}>
          {brand?.description}
        </Typography>
      </div>

      {/* {brand.cover_url && (
        <img
          src={brand.cover_url}
          alt=''
          style={{
            width: '100%',
            maxHeight: '400px',
            height: 'auto',
            objectFit: 'cover',
            objectPosition: 'left center',
            display: 'block',
            borderBottomRightRadius: '8px',
            borderBottomLeftRadius: '8px',
          }}
        />
      )} */}
    </div>
  );
};

export default BrandBanner;
